import { useEffect } from 'react';
import { X } from 'lucide-react';
import { confidenceTier, type DimensionDisplayData } from './dimensionHelpers';
import { DIMENSION_EVIDENCE } from '@shared/constants/evidence-status';

interface DimensionDetailPanelProps {
  dim: DimensionDisplayData | null;
  onClose: () => void;
}

export function DimensionDetailPanel({ dim, onClose }: DimensionDetailPanelProps) {
  // Close on Escape
  useEffect(() => {
    if (!dim) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dim, onClose]);

  const isHigh = dim ? dim.value >= 80 : false;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] transition-opacity duration-300"
        style={{
          opacity: dim ? 1 : 0,
          pointerEvents: dim ? 'auto' : 'none',
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
        }}
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div
        className="fixed top-0 right-0 bottom-0 z-[61] w-full max-w-md overflow-y-auto"
        style={{
          transform: dim ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
          background: 'linear-gradient(180deg, #111528, #0B0E1A)',
          borderLeft: '1px solid #2A2F4F',
        }}
      >
        {dim && (
          <div style={{ padding: '32px 28px' }}>
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-lg border border-white/10 text-content-muted hover:text-content cursor-pointer transition-colors"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <X size={16} />
            </button>

            {/* Dimension colour dot + name */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  background: dim.color,
                  boxShadow: `0 0 10px ${dim.color}50`,
                }}
              />
              <h2 className="font-heading font-bold text-xl m-0 text-content">{dim.name}</h2>
            </div>

            {/* Score + label hero */}
            <div
              className="rounded-xl border overflow-hidden mb-6"
              style={{
                background: `linear-gradient(135deg, ${dim.color}08, ${dim.color}03)`,
                borderColor: `${dim.color}25`,
                padding: '24px',
              }}
            >
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-xs text-content-dim font-medium uppercase tracking-wider mb-1">Your Score</div>
                  <div
                    className="font-code text-5xl font-bold leading-none"
                    style={{ color: isHigh ? '#F0A500' : '#fff' }}
                  >
                    {dim.value}
                  </div>
                </div>
                <div
                  className="text-sm font-semibold rounded-lg"
                  style={{
                    color: dim.color,
                    background: `${dim.color}15`,
                    padding: '6px 14px',
                  }}
                >
                  {dim.label}
                </div>
              </div>

              {/* Score bar */}
              <div className="h-1.5 rounded-full bg-dark-subtle overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    background: isHigh ? `linear-gradient(90deg, ${dim.color}, #F0A500)` : dim.color,
                    width: `${dim.value}%`,
                    transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                />
              </div>

              {/* Confidence sub-row */}
              <div className="flex items-center justify-between mt-3">
                <div className="text-[10px] uppercase tracking-wider text-content-dim font-medium">
                  Confidence
                </div>
                <div className="text-[11px] font-code text-content-muted">
                  {Math.round(dim.confidence * 100)}%
                  <span className="ml-1.5 text-content-dim">
                    · {confidenceTier(dim.confidence) === 'strong' ? 'Strong' : confidenceTier(dim.confidence) === 'building' ? 'Building' : 'Low'}
                  </span>
                </div>
              </div>
              <div className="h-[2px] rounded-full bg-dark-subtle overflow-hidden mt-1.5">
                <div
                  className="h-full rounded-full"
                  style={{
                    background: `${dim.color}80`,
                    width: `${Math.round(dim.confidence * 100)}%`,
                    transition: 'width 1s cubic-bezier(0.16, 1, 0.3, 1)',
                    opacity: 0.75,
                  }}
                />
              </div>
            </div>

            {/* What this means */}
            <div className="mb-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-content-dim mb-3">
                What this means for you
              </div>
              <p className="text-[15px] leading-[1.75] text-content-secondary m-0">
                {dim.summary}
              </p>
            </div>

            {/* Dimension description */}
            <div
              className="rounded-xl border border-dark-border p-4"
              style={{ background: 'rgba(255,255,255,0.02)' }}
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-content-dim mb-2">
                About this dimension
              </div>
              <p className="text-sm text-content-muted leading-relaxed m-0">
                {dim.description}
              </p>
              {/* Where this came from. A dimension only says "measured" once it has
                  passed both halves of the promotion bar — reliability AND an outcome
                  benefit on a beta cohort. Until then it is a preference, and says so.
                  See @shared/constants/evidence-status. */}
              <p className="text-xs text-content-dim leading-relaxed mt-3 mb-0 italic">
                {DIMENSION_EVIDENCE[dim.key]?.basis
                  ? `Based on ${DIMENSION_EVIDENCE[dim.key].basis}. `
                  : ''}
                {dim.evidence === 'measured'
                  ? "This one we observe as you learn."
                  : "This is how you told us you like to learn — a preference, not a measurement. It sharpens as CogniPrint sees what you actually do."}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
